import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PsychologyRoundedIcon from '@mui/icons-material/PsychologyRounded';
import {
  Box,
  Button,
  Checkbox, Chip,
  colors,
  Divider,
  FormControl,
  FormControlLabel, FormGroup, InputLabel,
  ListItemText,
  MenuItem, OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent, Slider,
  Stack,
  Switch, TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { ChangeEvent, FC, memo, useContext, useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import LicenseContext from 'src/contexts/LicenseContext';
import { ALL_ML_SOURCE_TYPES } from 'src/entities/admin/companyEntity';
import { RespectType, WeekDayEn } from 'src/entities/planningEntity';
import { HowToPlanning, PlanningConditions, PlanningPriority } from 'src/entities/PlanningHistory.entity';
import { RelaxedRuleVo } from 'src/vo/RelaxedRule.vo';

type Props = {
  screenName: string,
  updateBalancedLoading: (balancedLoading: boolean) => void;
  updateIsRespectRecent: (bool: boolean) => void;
  updateExcludeToll: (bool: boolean) => void;
  updateRespectType: (respectType: RespectType) => void;
  updateRespectDaysOfWeek: (days: WeekDayEn[]) => void;
  updateRespectOn: (on: string) => void;
  updateIsTimeTableBackward: (bool: boolean) => void;
  updateIncludeFutureDeliveries: (bool: boolean) => void;
  updateAutoSplitOrders: (bool: boolean) => void;
  updateDontExchangePreroute: (bool: boolean) => void;
  updateConcurrentAllOrNothing: (bool: boolean) => void;
  updateExpandWorkingStartMinutes: (minutes: number) => void;
  updateExpandWorkingEndMinutes: (minutes: number) => void;
  updateExpandLoadStartMinutes: (minutes: number) => void;
  updateExpandLoadEndMinutes: (minutes: number) => void;
  updateExpandUnloadStartMinutes: (minutes: number) => void;
  updateExpandUnloadEndMinutes: (minutes: number) => void;
  updateExpandMaxVolumeRate: (rate: number) => void;
  updateExpandMaxLoadCapacityRate: (rate: number) => void;
  planningRequest: (shiftIds: number[], orderIds: number[]) => void;
  onClose: () => void;
  selectedDeliveryIds: number[];
  selectedOrderIds: number[];
  startOn: string;
  relaxedRules: RelaxedRuleVo[];
  selectedRelaxedRules: RelaxedRuleVo[];
  setSelectedRelaxedRules: (value: RelaxedRuleVo[]) => void;
  setPriorityLargeTrucks: (priority: number) => void;
  priorityLargeTrucks: number;
  mlSourceTypes: number[];
  updateMLSourceType: (MLSourceType: number) => void;
  updateMLPlanning: (bool: boolean) => void;
  updateOptimizeForLoad: (bool: boolean) => void;
  updateForceNullBase: (bool: boolean) => void;
  updateTrunkTransportation: (bool: boolean) => void;
  updateActivateIncrementalMaxNumPistons: (bool: boolean) => void;
}

const PlanningPresenter: FC<Props> = memo((
  {
    screenName,
    updateRespectType,
    updateRespectDaysOfWeek,
    updateRespectOn,
    updateBalancedLoading,
    updateIsRespectRecent,
    updateExcludeToll,
    updateIsTimeTableBackward,
    updateIncludeFutureDeliveries,
    updateAutoSplitOrders,
    updateDontExchangePreroute,
    updateConcurrentAllOrNothing,
    updateExpandWorkingStartMinutes,
    updateExpandWorkingEndMinutes,
    updateExpandLoadStartMinutes,
    updateExpandLoadEndMinutes,
    updateExpandUnloadStartMinutes,
    updateExpandUnloadEndMinutes,
    updateExpandMaxVolumeRate,
    updateExpandMaxLoadCapacityRate,
    planningRequest,
    onClose,
    selectedDeliveryIds,
    selectedOrderIds,
    startOn,
    relaxedRules,
    selectedRelaxedRules,
    setSelectedRelaxedRules,
    setPriorityLargeTrucks,
    priorityLargeTrucks,
    mlSourceTypes,
    updateMLSourceType,
    updateMLPlanning,
    updateOptimizeForLoad,
    updateForceNullBase,
    updateTrunkTransportation,
    updateActivateIncrementalMaxNumPistons,
  }
) => {
  const licenseContext = useContext(LicenseContext);

  const [howToPlanning, setHowToPlanning] = useState<HowToPlanning>('最適化');
  const [planningPriority, setPlanningPriority] = useState<PlanningPriority>('指定なし');
  const [isForcePlanning, setIsForcePlanning] = useState<boolean>(false);
  const [ignoreWeightConstraints, setIgnoreWeightConstraints] = useState<boolean>(false);
  const [ignoreTimeConstraints, setIgnoreTimeConstraints] = useState<boolean>(false);
  const [respectRecentPlan, setRespectRecentPlan] = useState<boolean>(false);
  const [availabilityOfExpressway, setAvailabilityOfExpressway] = useState<boolean>(false);
  const [respectType, setRespectType] = useState<RespectType>('day_of_week');
  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState<WeekDayEn[]>([
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday'
  ]);
  const [respectOn, setRespectOn] = useState<string>(startOn);
  const [isTimeTableBackward, setIsTimeTableBackward] = useState<boolean>(false);
  const [includeFutureDeliveries, setIncludeFutureDeliveries] = useState<boolean>(false);
  const [autoSplitOrders, setAutoSplitOrders] = useState<boolean>(false);
  const [dontExchangePreroute, setDontExchangePreroute] = useState<boolean>(true);
  const [concurrentAllOrNothing, setConcurrentAllOrNothing] = useState<boolean>(true);
  const [activateIncrementalMaxNumPistons, setActivateIncrementalMaxNumPistons] = useState<boolean>(false);
  const [expandWorkingTimeStartMinutes, setExpandWorkingTimeStartMinutes] = useState<number>(0);
  const [expandWorkingTimeEndMinutes, setExpandWorkingTimeEndMinutes] = useState<number>(0);
  const [expandLoadStartMinutes, setExpandLoadStartMinutes] = useState<number>(0);
  const [expandLoadEndMinutes, setExpandLoadEndMinutes] = useState<number>(0);
  const [expandUnloadStartMinutes, setExpandUnloadStartMinutes] = useState<number>(0);
  const [expandUnloadEndMinutes, setExpandUnloadEndMinutes] = useState<number>(0);
  const [expandMaxVolumeRate, setExpandMaxVolumeRate] = useState<number>(0);
  const [expandMaxLoadCapacityRate, setExpandMaxLoadCapacityRate] = useState<number>(0);
  const [mlSourceType, setMLSourceType] = useState<number>(-1);

  const [canUseOldRespectPlanning, setCanUseOldRespectPlanning] = useState(false);
  const [useOldRespectPlanning, setUseOldRespectPlanning] = useState(false);

  const [optimizeForLoad, setOptimizeForLoad] = useState(false);
  const [forceNullBase, setForceNullBase] = useState(false);
  const [trunkTransportation, setTrunkTransportation] = useState(false);
  const [useNewALFeature, setUseNewALFeature] = useState(false);
  useEffect(() => {
    setCanUseOldRespectPlanning(['stg', 'dev'].includes(licenseContext.config?.app_env));
    setUseNewALFeature(['stg', 'dev'].includes(licenseContext.config?.app_env));
  }, [licenseContext.config?.app_env]);
  const useOldRespectPlanningOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setUseOldRespectPlanning(event.target.checked);
  };
  useEffect(() => {
    updateMLPlanning(!useOldRespectPlanning);
  }, [updateMLPlanning, useOldRespectPlanning]);
  const daysOfTheWeek: ({ ja: string; en: WeekDayEn })[] = [
    { en: 'monday', ja: '月' },
    { en: 'tuesday', ja: '火' },
    { en: 'wednesday', ja: '水' },
    { en: 'thursday', ja: '木' },
    { en: 'friday', ja: '金' },
    { en: 'saturday', ja: '土' },
    { en: 'sunday', ja: '日' }
  ];
  const selectedDayOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedDaysOfWeek(
        [
          ...selectedDaysOfWeek.filter((it) => it !== event.target.name as WeekDayEn),
          event.target.name as WeekDayEn
        ]
      );
    } else {
      setSelectedDaysOfWeek(
        [
          ...selectedDaysOfWeek.filter((it) => it !== event.target.name as WeekDayEn),
        ]
      );
    }
  };

const handleSelectedRelaxRules = (event: SelectChangeEvent<typeof selectedRelaxedRules>) => {
    const {
      target: { value },
    } = event;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    setSelectedRelaxedRules(typeof value === 'string' ? value.split(',') : value);
  };
  const selectRelaxRuleButtonOnClick = () => {
    const values = relaxedRules.some((it) => selectedRelaxedRules.includes(it)) ? [] : relaxedRules;

    setSelectedRelaxedRules(values);
  };

  const howToPlanningOnChange = (event: SelectChangeEvent) => {
    setHowToPlanning(event.target.value as HowToPlanning);
  };

  const planningPriorityOnChange = (event: SelectChangeEvent) => {
    setPlanningPriority(event.target.value as PlanningPriority);
  };

  const availabilityOfExpresswayOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setAvailabilityOfExpressway(event.target.checked);
  };

  const includeFutureDeliveriesOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIncludeFutureDeliveries(event.target.checked);
  };

  const splitOrdersOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setAutoSplitOrders(event.target.checked);
  };

  const dontExchangePrerouteOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDontExchangePreroute(event.target.checked);
  };

  const concurrentAllOrNothingOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setConcurrentAllOrNothing(event.target.checked);
  };

  const activateIncrementalMaxNumPistonsOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setActivateIncrementalMaxNumPistons(event.target.checked);
  };

  const respectRecentPlanOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setRespectRecentPlan(event.target.checked);
  };

  const respectTypeOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setRespectType(event.target.value as RespectType);
  };

  const specificDayOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setRespectOn(event.target.value);
  };

  const mlSourceTypeChange = (event: SelectChangeEvent) => {
    setMLSourceType(Number(event.target.value));
  };

  const optimizeForLoadOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setOptimizeForLoad(event.target.checked);
  };

  const forceNullBaseOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setForceNullBase(event.target.checked);
  };

  const trunkTransportationOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTrunkTransportation(event.target.checked);
  };

  const onClick = () => {
    ReactGA.event('executePlanning', { screen_name: screenName, button_name: '自動配車' });
    onClose();

    planningRequest(
      selectedDeliveryIds,
      selectedOrderIds,
    );
  };

  useEffect(() => {
    updateExpandWorkingStartMinutes(expandWorkingTimeStartMinutes);
  }, [expandWorkingTimeStartMinutes, updateExpandWorkingStartMinutes]);

  useEffect(() => {
    updateExpandWorkingEndMinutes(expandWorkingTimeEndMinutes);
  }, [expandWorkingTimeEndMinutes, updateExpandWorkingEndMinutes]);

  useEffect(() => {
    updateExpandLoadStartMinutes(expandLoadStartMinutes);
  }, [expandLoadStartMinutes]);

  useEffect(() => {
    updateExpandLoadEndMinutes(expandLoadEndMinutes);
  }, [expandLoadEndMinutes]);

  useEffect(() => {
    updateExpandUnloadStartMinutes(expandUnloadStartMinutes);
  }, [expandUnloadStartMinutes]);

  useEffect(() => {
    updateExpandUnloadEndMinutes(expandUnloadEndMinutes);
  }, [expandUnloadEndMinutes]);

  useEffect(() => {
    updateExpandMaxVolumeRate(expandMaxVolumeRate);
  }, [expandMaxVolumeRate]);

  useEffect(() => {
    updateExpandMaxLoadCapacityRate(expandMaxLoadCapacityRate);
  }, [expandMaxLoadCapacityRate]);

  useEffect(() => {
    updateIsRespectRecent(respectRecentPlan);
  }, [respectRecentPlan]);

  useEffect(() => {
    updateExcludeToll(!availabilityOfExpressway);
  }, [availabilityOfExpressway]);

  useEffect(() => {
    updateRespectType(respectType);
  }, [respectType]);

  useEffect(() => {
    updateRespectDaysOfWeek(selectedDaysOfWeek);
  }, [selectedDaysOfWeek]);

  useEffect(() => {
    updateRespectOn(respectOn);
  }, [respectOn]);

  useEffect(() => {
    updateIsTimeTableBackward(isTimeTableBackward);
  }, [isTimeTableBackward]);

  useEffect(() => {
    updateIncludeFutureDeliveries(includeFutureDeliveries);
  }, [includeFutureDeliveries]);

  useEffect(() => {
    updateAutoSplitOrders(autoSplitOrders);
  }, [autoSplitOrders, updateAutoSplitOrders]);

  useEffect(() => {
    updateDontExchangePreroute(dontExchangePreroute);
  }, [dontExchangePreroute, updateDontExchangePreroute]);

  useEffect(() => {
    updateConcurrentAllOrNothing(concurrentAllOrNothing);
  }, [concurrentAllOrNothing, updateConcurrentAllOrNothing]);

  useEffect(() => {
    updateActivateIncrementalMaxNumPistons(activateIncrementalMaxNumPistons);
  }, [activateIncrementalMaxNumPistons]);

  useEffect(() => {
    updateMLSourceType(mlSourceType);
  }, [mlSourceType, updateMLSourceType]);

  useEffect(() => {
    updateOptimizeForLoad(optimizeForLoad);
  }, [optimizeForLoad, updateOptimizeForLoad]);

  useEffect(() => {
    updateForceNullBase(forceNullBase);
  }, [forceNullBase, updateForceNullBase]);

  useEffect(() => {
    updateTrunkTransportation(trunkTransportation);
  }, [trunkTransportation, updateTrunkTransportation]);

  useEffect(() => {
    if (howToPlanning === '平準化') {
      updateBalancedLoading(true);
    } else {
      updateBalancedLoading(false);
    }
  }, [howToPlanning, updateBalancedLoading]);

  useEffect(() => {
    const json = localStorage.getItem('PlanningPresenterSettings');

    if (!json) {
      return;
    }

    const planning = JSON.parse(json) as unknown as PlanningConditions;

    setHowToPlanning(
      ['最適化', '平準化'].includes(planning.howToPlanning) ? planning.howToPlanning : '最適化'
    );
    setPlanningPriority((prev) =>
      planning.planningPriority ?? prev
    );
    setIsForcePlanning((prev) =>
      planning.isForcePlanning ?? prev
    );
    setIgnoreWeightConstraints((prev) =>
      planning.ignoreWeightConstraints ?? prev
    );
    setIgnoreTimeConstraints((prev) =>
      planning.ignoreTimeConstraints ?? prev
    );
    setRespectRecentPlan((prev) =>
      planning.respectRecentPlan ?? prev
    );
    setAvailabilityOfExpressway((prev) =>
      planning.availabilityOfExpressway ?? prev
    );
    setRespectType((prev) =>
      planning.respectType ?? prev
    );
    setSelectedDaysOfWeek((prev) =>
      planning.selectedDaysOfWeek ?? prev
    );
    setRespectOn((prev) =>
      planning.respectOn ?? prev
    );
    setIsTimeTableBackward((prev) =>
      planning.isTimeTableBackward ?? prev
    );
    setIncludeFutureDeliveries((prev) =>
      planning.includeFutureDeliveries ?? prev
    );
    setAutoSplitOrders(
      false
    );
    setDontExchangePreroute(
      planning.dontExchangePreroute === undefined ? true : planning.dontExchangePreroute,
    );
    setConcurrentAllOrNothing((prev) =>
      planning.concurrentAllOrNothing ?? prev
    );
    setActivateIncrementalMaxNumPistons((prev) =>
      planning.activateIncrementalMaxNumPistons ?? prev
    );
    setSelectedRelaxedRules(
      planning.selectedRelaxedRules ?? []
    );
    setPriorityLargeTrucks(
      planning.priorityLargeTrucks ?? 0
    );
    setExpandWorkingTimeStartMinutes((prev) =>
      planning.expandWorkingTimeStartMinutes ?? prev
    );
    setExpandWorkingTimeEndMinutes((prev) =>
      planning.expandWorkingTimeEndMinutes ?? prev
    );
    setExpandLoadStartMinutes((prev) =>
      planning.expandLoadStartMinutes ?? prev
    );
    setExpandLoadEndMinutes((prev) =>
      planning.expandLoadEndMinutes ?? prev
    );
    setExpandUnloadStartMinutes((prev) =>
      planning.expandUnloadStartMinutes ?? prev
    );
    setExpandUnloadEndMinutes((prev) =>
      planning.expandUnloadEndMinutes ?? prev
    );
    setExpandMaxVolumeRate((prev) =>
      planning.expandMaxVolumeRate ?? prev
    );
    setExpandMaxLoadCapacityRate((prev) =>
      planning.expandMaxLoadCapacityRate ?? prev
    );
    setOptimizeForLoad(
      planning.optimizeForLoad === undefined ? false : planning.optimizeForLoad
    );
    setForceNullBase(
      planning.forceNullBase === undefined ? false : planning.forceNullBase
    );
    setTrunkTransportation(
      !licenseContext.config.use_trunk_transportation || planning.trunkTransportation === undefined ? false : planning.trunkTransportation
    );
  }, [licenseContext.config, setPriorityLargeTrucks, setSelectedRelaxedRules]);

  useEffect(() => {
    const obj = {
      howToPlanning,
      planningPriority,
      isForcePlanning,
      ignoreWeightConstraints,
      ignoreTimeConstraints,
      respectRecentPlan,
      availabilityOfExpressway,
      respectType,
      selectedDaysOfWeek,
      respectOn,
      isTimeTableBackward,
      includeFutureDeliveries,
      splitOrders: autoSplitOrders,
      selectedRelaxedRules,
      priorityLargeTrucks,
      expandWorkingTimeStartMinutes,
      expandWorkingTimeEndMinutes,
      expandLoadStartMinutes,
      expandLoadEndMinutes,
      expandUnloadStartMinutes,
      expandUnloadEndMinutes,
      expandMaxVolumeRate,
      expandMaxLoadCapacityRate,
      dontExchangePreroute,
      concurrentAllOrNothing,
      optimizeForLoad,
      forceNullBase,
      trunkTransportation,
      activateIncrementalMaxNumPistons,
    };

    localStorage.setItem('PlanningPresenterSettings', JSON.stringify(obj));
  }, [
    howToPlanning,
    planningPriority,
    isForcePlanning,
    ignoreWeightConstraints,
    ignoreTimeConstraints,
    respectRecentPlan,
    availabilityOfExpressway,
    respectType,
    selectedDaysOfWeek,
    respectOn,
    isTimeTableBackward,
    includeFutureDeliveries,
    autoSplitOrders,
    selectedRelaxedRules,
    priorityLargeTrucks,
    expandWorkingTimeStartMinutes,
    expandWorkingTimeEndMinutes,
    expandLoadStartMinutes,
    expandLoadEndMinutes,
    expandUnloadStartMinutes,
    expandUnloadEndMinutes,
    expandMaxVolumeRate,
    expandMaxLoadCapacityRate,
    dontExchangePreroute,
    concurrentAllOrNothing,
    optimizeForLoad,
    forceNullBase,
    trunkTransportation,
    activateIncrementalMaxNumPistons
  ]);

  useEffect(() => {
    if (!selectedRelaxedRules) return;
    if (!selectedRelaxedRules.length) return;

    if (selectedRelaxedRules.includes('労働時間')) {
      setExpandWorkingTimeStartMinutes(0);
      setExpandWorkingTimeEndMinutes(0);
    }
    if (selectedRelaxedRules.includes('指定時間')) {
      setExpandLoadStartMinutes(0);
      setExpandLoadEndMinutes(0);
      setExpandUnloadStartMinutes(0);
      setExpandUnloadEndMinutes(0);
    }

    if (selectedRelaxedRules.includes('重量')) {
      setExpandMaxLoadCapacityRate(0);
    }

    if (selectedRelaxedRules.includes('体積')) {
      setExpandMaxVolumeRate(0);
    }
  }, [selectedRelaxedRules]);

  return (
    <Stack
      sx={{
        p: 2
      }} spacing={1}
      divider={<Divider />}
    >
      <Stack spacing={2}>
        <Stack>
          <Typography
            variant="h5"
            gutterBottom
          >
            計算方法を選択してください
          </Typography>
          <FormControl
            fullWidth
            variant="standard"
          >
            <Select
              value={howToPlanning}
              onChange={howToPlanningOnChange}
            >
              <MenuItem value="最適化" sx={{ m: 0 }}>最適化 使用するトラック台数と時間距離を最小化します</MenuItem>
              <MenuItem value="平準化" sx={{ m: 0 }}>平準化 割り付ける荷物を均等化します</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        <Stack
          direction="column"
        >
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            gap={3}
          >
            <FormControl>
              <RadioGroup
                row
                value={planningPriority}
                onChange={planningPriorityOnChange}
              >
                <FormControlLabel value="指定なし" control={<Radio />} label="指定なし" />
                <FormControlLabel value="近隣優先" control={<Radio />} label="近隣優先" />
                <FormControlLabel value="遠方優先" control={<Radio />} label="遠方優先" />
              </RadioGroup>
            </FormControl>
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              gap={0.5}
              flexGrow={1}
            >
              <Stack
                minWidth="4rem"
              >
                <Typography>
                  小型優先
                </Typography>
              </Stack>
              <Slider
                value={priorityLargeTrucks * 100}
                onChange={(_, value) => setPriorityLargeTrucks(value / 100)}
                getAriaValueText={(value) => `${value * 100}%`}
                step={10}
                min={0}
                max={100}
                valueLabelDisplay="auto"
              />
              <Stack
                minWidth="4rem"
              >
                <Typography>
                  大型優先
                </Typography>
              </Stack>
            </Stack>
            <Stack>
              <FormControlLabel
                onChange={availabilityOfExpresswayOnChange}
                control={<Switch checked={availabilityOfExpressway} />}
                label="有料道路使用"
              />
            </Stack>
            <Stack>
              <FormControlLabel
                onChange={includeFutureDeliveriesOnChange}
                control={<Switch checked={includeFutureDeliveries} />}
                label="宵積み有効"
              />
            </Stack>
          </Stack>
          <Stack>
            <FormControlLabel
              onChange={splitOrdersOnChange}
              control={<Checkbox checked={autoSplitOrders} />}
              label="案件の重量・体積が、選択したトラックの最大積載量を超える場合に案件を自動で分割する"
            />
          </Stack>
          <Stack direction="row" gap={3}>
            <Stack
              direction="row"
              alignItems="center"
            >
              <FormControlLabel
                key="dontExchangePreroute"
                onChange={dontExchangePrerouteOnChange}
                control={<Switch checked={dontExchangePreroute} />}
                label="現在の配車を維持して追加配車"
              />
              <Tooltip arrow title="新しいオーダーを追加配車する際、既存のドライバー割り当てを維持するか、ドライバー間で割り当てを変更するかを選択できます。">
                <InfoOutlinedIcon sx={{ color: colors.blue[900] }} />
              </Tooltip>
            </Stack>
            <Stack>
              <FormControlLabel
                key="concurrentAllOrNothing"
                onChange={concurrentAllOrNothingOnChange}
                control={<Switch checked={concurrentAllOrNothing} />}
                label="同一ロケーションで複数台になってしまう場合は割り当てない"
              />
            </Stack>
          </Stack>
          <Stack direction="row" gap={3}>
            <Stack
              direction="row"
              alignItems="center"
            >
              <FormControlLabel
                key="activateIncrementalMaxNumPistons"
                onChange={activateIncrementalMaxNumPistonsOnChange}
                control={<Switch checked={activateIncrementalMaxNumPistons} />}
                label="回転数を平準化する"
              />
            </Stack>
          </Stack>
        </Stack>

        {licenseContext.config.use_ai_planning && (
          <Stack gap={1} sx={{ py: 1 }}>
            <Stack direction="row" alignItems="center" gap={2}>
              <FormControlLabel
                onChange={respectRecentPlanOnChange}
                control={<Switch checked={respectRecentPlan} />}
                label="過去の計画を考慮して配車する"
                sx={{ whiteSpace: 'nowrap' }}
              />
              {mlSourceTypes.length > 0 && (
                <Stack gap={1} direction="row" alignItems="center" sx={{ width: '100%' }}>
                  <Stack sx={{ whiteSpace: 'nowrap' }}>学習対象データを選択</Stack>
                  <Stack sx={{ width: '100%' }}>
                    <Select
                      value={String(mlSourceType)}
                      onChange={mlSourceTypeChange}
                      disabled={!respectRecentPlan}
                      variant="standard"
                      fullWidth
                    >
                      <MenuItem value="-1">デフォルト設定</MenuItem>
                      {mlSourceTypes.map((it) => (
                        <MenuItem
                          value={it}
                          key={`mlSourceTypeChange-ALL_ML_SOURCE_TYPES-${it}`}
                        >
                          {ALL_ML_SOURCE_TYPES[it]}
                        </MenuItem>
                      ))}
                    </Select>
                  </Stack>
                </Stack>
              )}
            </Stack>
            {(canUseOldRespectPlanning && (
              <Stack>
                <FormControlLabel
                  onChange={useOldRespectPlanningOnChange}
                  control={<Switch checked={useOldRespectPlanning} />}
                  label="旧バージョンの記憶配車を利用する"
                  sx={{ whiteSpace: 'nowrap' }}
                  disabled={!respectRecentPlan}
                />
              </Stack>
            ))}
            <Stack>
              <FormControl>
                <RadioGroup onChange={respectTypeOnChange} value={respectType}>
                  <Stack direction="row" spacing={2}>
                    {(!canUseOldRespectPlanning || !useOldRespectPlanning) && (
                      <FormControlLabel value="day_of_week" label="日付指定なし" control={<Radio />} disabled={!respectRecentPlan} />
                    )}
                    <Stack
                      alignItems="center"
                      direction="row"
                      spacing={2}
                    >
                      <FormControlLabel
                        value="specific_day"
                        label="日付指定"
                        control={
                          <Radio />
                        }
                        disabled={!respectRecentPlan}
                      />
                      <TextField
                        type="date"
                        size="small"
                        value={respectOn}
                        onChange={specificDayOnChange}
                        disabled={
                          !respectRecentPlan || respectType !== 'specific_day'
                        }
                      />
                    </Stack>
                    {canUseOldRespectPlanning && useOldRespectPlanning && (
                      <Stack direction="row">
                        <FormControlLabel value="day_of_week" label="曜日指定" control={<Radio />} disabled={!respectRecentPlan} />
                        <FormGroup row>
                          {
                            daysOfTheWeek.map(({ en, ja }) => (
                              <FormControlLabel
                                key={en}
                                label={ja}
                                control={(
                                  <Checkbox
                                    name={en}
                                    checked={selectedDaysOfWeek.includes(en)}
                                    onChange={selectedDayOnChange}
                                    disabled={!respectRecentPlan || respectType !== 'day_of_week'}
                                  />
                                )}
                              />
                            ))
                          }
                        </FormGroup>
                      </Stack>
                    )}
                  </Stack>
                </RadioGroup>
              </FormControl>
            </Stack>
          </Stack>
        )}
        <Stack>
          <Typography>
            制約緩和
          </Typography>
          <Stack
            spacing={0.5}
          >
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              gap={1}
            >
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                gap={0.5}
                flexGrow={1}
              >
                <Stack
                  sx={{
                    minWidth: '4rem'
                  }}
                >
                  勤務開始
                </Stack>
                <Slider
                  step={10}
                  min={0}
                  max={120}
                  disabled={selectedRelaxedRules.includes('労働時間')}
                  value={expandWorkingTimeStartMinutes}
                  onChange={(_, value) => setExpandWorkingTimeStartMinutes(value)}
                />
                <TextField
                  size="small"
                  inputProps={{
                    step: 1,
                    min: 0,
                    max: 120,
                    type: 'numeric',
                    inputMode: 'numeric',
                  }}
                  sx={{
                    width: '10rem'
                  }}
                  disabled={selectedRelaxedRules.includes('労働時間')}
                  value={expandWorkingTimeStartMinutes}
                  onChange={(event) => {
                    const maybe = Number(event.target.value);
                    if (Number.isNaN(maybe)) return;

                    setExpandWorkingTimeStartMinutes(Number(event.target.value));
                  }}
                />
                <Stack
                  sx={{
                    width: '12rem'
                  }}
                >
                  分前まで緩和
                </Stack>
              </Stack>
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                gap={0.5}
                flexGrow={1}
              >
                <Stack
                  sx={{
                    minWidth: '4rem'
                  }}
                >
                  勤務終了
                </Stack>
                <Slider
                  step={10}
                  min={0}
                  max={120}
                  disabled={selectedRelaxedRules.includes('労働時間')}
                  value={expandWorkingTimeEndMinutes}
                  onChange={(_, value) => setExpandWorkingTimeEndMinutes(value)}
                />
                <TextField
                  size="small"
                  inputProps={{
                    step: 1,
                    min: 0,
                    max: 120,
                    type: 'numeric',
                    inputMode: 'numeric',
                  }}
                  sx={{
                    width: '10rem'
                  }}
                  disabled={selectedRelaxedRules.includes('労働時間')}
                  value={expandWorkingTimeEndMinutes}
                  onChange={(event) => {
                    const maybe = Number(event.target.value);
                    if (Number.isNaN(maybe)) return;

                    setExpandWorkingTimeEndMinutes(Number(event.target.value));
                  }}
                />
                <Stack
                  sx={{
                    width: '12rem'
                  }}
                >
                  分後まで緩和
                </Stack>
              </Stack>
            </Stack>
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              gap={1}
            >
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                gap={0.5}
                flexGrow={1}
              >
                <Stack
                  sx={{
                    minWidth: '4rem'
                  }}
                >
                  集荷開始
                </Stack>
                <Slider
                  step={10}
                  min={0}
                  max={120}
                  disabled={selectedRelaxedRules.includes('指定時間')}
                  value={expandLoadStartMinutes}
                  onChange={(_, value) => setExpandLoadStartMinutes(value)}
                />
                <TextField
                  size="small"
                  inputProps={{
                    step: 1,
                    min: 0,
                    max: 120,
                    type: 'numeric',
                    inputMode: 'numeric',
                  }}
                  sx={{
                    width: '10rem'
                  }}
                  disabled={selectedRelaxedRules.includes('指定時間')}
                  value={expandLoadStartMinutes}
                  onChange={(event) => {
                    const maybe = Number(event.target.value);
                    if (Number.isNaN(maybe)) return;

                    setExpandLoadStartMinutes(Number(event.target.value));
                  }}
                />
                <Stack
                  sx={{
                    width: '12rem'
                  }}
                >
                  分前まで緩和
                </Stack>
              </Stack>
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                gap={0.5}
                flexGrow={1}
              >
                <Stack
                  sx={{
                    minWidth: '4rem'
                  }}
                >
                  集荷締切
                </Stack>
                <Slider
                  step={10}
                  min={0}
                  max={120}
                  disabled={selectedRelaxedRules.includes('指定時間')}
                  value={expandLoadEndMinutes}
                  onChange={(_, value) => setExpandLoadEndMinutes(value)}
                />
                <TextField
                  size="small"
                  inputProps={{
                    step: 1,
                    min: 0,
                    max: 120,
                    type: 'numeric',
                    inputMode: 'numeric',
                  }}
                  sx={{
                    width: '10rem'
                  }}
                  disabled={selectedRelaxedRules.includes('指定時間')}
                  value={expandLoadEndMinutes}
                  onChange={(event) => {
                    const maybe = Number(event.target.value);
                    if (Number.isNaN(maybe)) return;

                    setExpandLoadEndMinutes(Number(event.target.value));
                  }}
                />
                <Stack
                  sx={{
                    width: '12rem'
                  }}
                >
                  分後まで緩和
                </Stack>
              </Stack>
            </Stack>
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              gap={1}
            >
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                gap={0.5}
                flexGrow={1}
              >
                <Stack
                  sx={{
                    minWidth: '4rem'
                  }}
                >
                  納品開始
                </Stack>
                <Slider
                  step={10}
                  min={0}
                  max={120}
                  disabled={selectedRelaxedRules.includes('指定時間')}
                  value={expandUnloadStartMinutes}
                  onChange={(_, value) => setExpandUnloadStartMinutes(value)}
                />
                <TextField
                  size="small"
                  inputProps={{
                    step: 1,
                    min: 0,
                    max: 120,
                    type: 'numeric',
                    inputMode: 'numeric',
                  }}
                  sx={{
                    width: '10rem'
                  }}
                  disabled={selectedRelaxedRules.includes('指定時間')}
                  value={expandUnloadStartMinutes}
                  onChange={(event) => {
                    const maybe = Number(event.target.value);
                    if (Number.isNaN(maybe)) return;

                    setExpandUnloadStartMinutes(Number(event.target.value));
                  }}
                />
                <Stack
                  sx={{
                    width: '12rem'
                  }}
                >
                  分前まで緩和
                </Stack>
              </Stack>
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                gap={0.5}
                flexGrow={1}
              >
                <Stack
                  sx={{
                    minWidth: '4rem'
                  }}
                >
                  納品締切
                </Stack>
                <Slider
                  step={10}
                  min={0}
                  max={120}
                  disabled={selectedRelaxedRules.includes('指定時間')}
                  value={expandUnloadEndMinutes}
                  onChange={(_, value) => setExpandUnloadEndMinutes(value)}
                />
                <TextField
                  size="small"
                  inputProps={{
                    step: 1,
                    min: 0,
                    max: 120,
                    type: 'numeric',
                    inputMode: 'numeric',
                  }}
                  sx={{
                    width: '10rem'
                  }}
                  disabled={selectedRelaxedRules.includes('指定時間')}
                  value={expandUnloadEndMinutes}
                  onChange={(event) => {
                    const maybe = Number(event.target.value);
                    if (Number.isNaN(maybe)) return;

                    setExpandUnloadEndMinutes(Number(event.target.value));
                  }}
                />
                <Stack
                  sx={{
                    width: '12rem'
                  }}
                >
                  分後まで緩和
                </Stack>
              </Stack>
            </Stack>
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              gap={1}
            >
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                gap={0.5}
                flexGrow={1}
              >
                <Stack
                  sx={{
                    minWidth: '4rem'
                  }}
                >
                  積載重量
                </Stack>
                <Slider
                  step={10}
                  min={0}
                  max={100}
                  disabled={selectedRelaxedRules.includes('重量')}
                  value={expandMaxLoadCapacityRate}
                  onChange={(_, value) => setExpandMaxLoadCapacityRate(value)}
                />
                <TextField
                  size="small"
                  inputProps={{
                    step: 1,
                    min: 0,
                    max: 100,
                    type: 'numeric',
                    inputMode: 'numeric',
                  }}
                  sx={{
                    width: '10rem'
                  }}
                  disabled={selectedRelaxedRules.includes('重量')}
                  value={expandMaxLoadCapacityRate}
                  onChange={(event) => {
                    const maybe = Number(event.target.value);
                    if (Number.isNaN(maybe)) return;

                    setExpandMaxLoadCapacityRate(Number(event.target.value));
                  }}
                />
                <Stack
                  sx={{
                    width: '12rem'
                  }}
                >
                  ％まで緩和
                </Stack>
              </Stack>
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                gap={0.5}
                flexGrow={1}
              >
                <Stack
                  sx={{
                    minWidth: '4rem'
                  }}
                >
                  積載体積
                </Stack>
                <Slider
                  step={10}
                  min={0}
                  max={100}
                  disabled={selectedRelaxedRules.includes('体積')}
                  value={expandMaxVolumeRate}
                  onChange={(_, value) => setExpandMaxVolumeRate(value)}
                />
                <TextField
                  size="small"
                  inputProps={{
                    step: 1,
                    min: 0,
                    max: 100,
                    type: 'numeric',
                    inputMode: 'numeric',
                  }}
                  sx={{
                    width: '10rem'
                  }}
                  disabled={selectedRelaxedRules.includes('体積')}
                  value={expandMaxVolumeRate}
                  onChange={(event) => {
                    const maybe = Number(event.target.value);
                    if (Number.isNaN(maybe)) return;

                    setExpandMaxVolumeRate(Number(event.target.value));
                  }}
                />
                <Stack
                  sx={{
                    width: '12rem'
                  }}
                >
                  ％まで緩和
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Stack>

        <Stack>
          <Stack
            direction="row"
          >
            <Button
              onClick={selectRelaxRuleButtonOnClick}
              size="small"
            >
              {
                selectedRelaxedRules.length ? '選択した制約無効をリセットする' : 'すべての制約を無効にする'
              }
            </Button>
          </Stack>
          <FormControl>
            <InputLabel>
              無効にする制約
            </InputLabel>
            <Select<RelaxedRuleVo[]>
              multiple
              value={selectedRelaxedRules}
              onChange={handleSelectedRelaxRules}
              input={<OutlinedInput label="無効にする制約" />}
              renderValue={(selected) => (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5
                  }}
                >
                  {
                    selected.map((value) => (
                      <Chip
                        key={value}
                        label={value}
                      />
                    ))
                  }
                </Box>
              )}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 600,
                  },
                },
              }}
            >
              {
                relaxedRules.map((it) => (
                  <MenuItem key={it} value={it}>
                    <Checkbox checked={selectedRelaxedRules.indexOf(it) > -1} />
                    <ListItemText primary={it} />
                  </MenuItem>
                ))
              }
            </Select>
          </FormControl>
        </Stack>
      </Stack>
      <Stack>
        <Button
          size="large"
          variant="contained"
          startIcon={<PsychologyRoundedIcon />}
          onClick={onClick}
        >
          実行する
        </Button>
      </Stack>
    </Stack>
  );
});

export default PlanningPresenter;
